<script setup lang="ts">
import { defineProps, computed, toRef } from "vue";
import { useGridGenerator } from "@/views/Calculators/ApartmentPlan/composables/useGridGenerator";
import {
  CORNER_LABEL,
  HORIZONTAL_LABEL,
  VERTICAL_LABELS,
} from "@/constants/twelweMountainsLables";
import { BoundingBox } from "@/types/stage";

const props = defineProps<{
  boundingBox: BoundingBox | null;
  moveLabelsMode: boolean;
}>();

const boundingBoxRef = toRef(props, "boundingBox");

const { gridLines } = useGridGenerator(boundingBoxRef, 6, 5, "blue", "blue");

const mountainsLabels = computed(() => {
  if (!props.boundingBox || !gridLines.value) return [];

  const labelOffset = 20;
  const { minX, minY, maxX, maxY } = props.boundingBox;
  const labels: { x: number; y: number; label: string }[] = [
    {
      x: minX + labelOffset * 0.3,
      y: minY + labelOffset * 0.3,
      label: CORNER_LABEL["topLeft"],
    },
    {
      x: maxX - labelOffset * 0.3,
      y: minY + labelOffset * 0.3,
      label: CORNER_LABEL["topRight"],
    },
    {
      x: minX + labelOffset * 0.3,
      y: maxY - labelOffset * 0.3,
      label: CORNER_LABEL["bottomLeft"],
    },
    {
      x: maxX - labelOffset * 0.3,
      y: maxY - labelOffset * 0.3,
      label: CORNER_LABEL["bottomRight"],
    },
  ];
  const verticals: { x: number; line: [number, number, number, number] }[] = [];
  const horizontals: { y: number; line: [number, number, number, number] }[] =
    [];
  for (const line of gridLines.value) {
    const [x1, y1, x2, y2] = line.points;
    if (x1 === x2) {
      verticals.push({ x: x1, line: [x1, y1, x2, y2] });
    } else if (y1 === y2) {
      horizontals.push({ y: y1, line: [x1, y1, x2, y2] });
    }
  }
  verticals.sort((a, b) => a.x - b.x);
  horizontals.sort((a, b) => a.y - b.y);

  verticals.map((line, i) => {
    const verticalLabel = VERTICAL_LABELS[i];
    labels.push({
      x: line.x,
      y: minY - labelOffset,
      label: verticalLabel["top"],
    });
    labels.push({
      x: line.x,
      y: maxY + labelOffset,
      label: verticalLabel["down"],
    });
  });
  horizontals.map((line, i) => {
    const horizontalLabel = HORIZONTAL_LABEL[i];
    labels.push({
      x: minX - labelOffset,
      y: line.y,
      label: horizontalLabel["left"],
    });
    labels.push({
      x: maxX + labelOffset,
      y: line.y,
      label: horizontalLabel["right"],
    });
  });

  return labels;
});
</script>

<template>
  <v-text
    v-for="(label, i) in mountainsLabels"
    :key="'label-' + i"
    :config="{
      x: label.x,
      y: label.y,
      text: label.label,
      fontSize: 18,
      fill: 'black',
      draggable: moveLabelsMode,
    }"
  />
</template>
